import { z } from "zod";
export const CustomerType = z.enum(["BUSINESS", "PRIVATE"]);
export const LeadStatus = z.enum([
    "NEW",
    "CONTACTED",
    "QUOTED",
    "WON",
    "LOST",
    "SPAM",
]);
export const ContactStatus = z.enum(["NEW", "CONTACTED", "SPAM"]);
export const SpaceType = z.enum([
    "OFFICE",
    "COWORKING",
    "PROJECT_OFFICE",
    "PRIVATE_HOME",
    "APARTMENT",
    "STAIRCASE",
    "CLINIC",
    "SHOP_SHOWROOM",
    "MOVE_IN_MOVE_OUT",
    "DEEP_CLEANING",
    "RENOVATION_FINAL_CLEANING",
    "OTHER",
]);
export const CleaningFrequency = z.enum([
    "ONE_TIME",
    "WEEKLY",
    "TWO_TIMES_PER_WEEK",
    "THREE_TIMES_PER_WEEK",
    "FIVE_TIMES_PER_WEEK",
    "MONTHLY",
    "NOT_SURE",
]);
export const PreferredCleaningTime = z.enum([
    "MORNING",
    "DAYTIME",
    "EVENING",
    "WEEKEND",
    "FLEXIBLE",
]);
const requiredText = (label, max = 200) => z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} is too long`);
const optionalText = (max = 300) => z.string().trim().max(max).optional().or(z.literal(""));
export const consentSchema = z.literal(true, {
    errorMap: () => ({
        message: "Consent is required before we can contact you.",
    }),
});
export const quoteLeadSchema = z.object({
    customerType: CustomerType,
    companyName: optionalText(160),
    contactName: requiredText("Contact name", 160),
    email: z.string().trim().email("Enter a valid email address").max(200),
    phone: z
        .string()
        .trim()
        .min(6, "Enter a valid phone number")
        .max(30, "Phone number is too long"),
    spaceType: SpaceType,
    address: requiredText("Location/address", 240),
    approximateSize: z.coerce
        .number()
        .int()
        .positive()
        .max(100000)
        .optional()
        .or(z.literal("")),
    numberOfRooms: z.coerce
        .number()
        .int()
        .min(0)
        .max(10000)
        .optional()
        .or(z.literal("")),
    numberOfEmployeesOrUsers: z.coerce
        .number()
        .int()
        .min(0)
        .max(100000)
        .optional()
        .or(z.literal("")),
    numberOfToiletsOrBathrooms: z.coerce
        .number()
        .int()
        .min(0)
        .max(1000)
        .optional()
        .or(z.literal("")),
    cleaningFrequency: CleaningFrequency,
    preferredCleaningTime: PreferredCleaningTime,
    message: optionalText(3000),
    consent: consentSchema,
    website: z.string().max(0).optional(),
});
export const contactMessageSchema = z.object({
    name: requiredText("Name", 160),
    company: optionalText(160),
    email: z.string().trim().email("Enter a valid email address").max(200),
    phone: z.string().trim().min(6, "Enter a valid phone number").max(30),
    message: requiredText("Message", 3000),
    consent: consentSchema,
    website: z.string().max(0).optional(),
});
export const loginSchema = z.object({
    email: z.string().trim().email(),
    password: z.string().min(8).max(200),
});
export const adminLeadUpdateSchema = z.object({
    status: LeadStatus.optional(),
    internalNotes: optionalText(5000),
});
export const adminContactUpdateSchema = z.object({
    status: ContactStatus.optional(),
    internalNotes: optionalText(5000),
});
export const blogQuerySchema = z.object({
    locale: z.enum(["da", "en"]).optional(),
    page: z.coerce.number().int().positive().default(1).optional(),
    limit: z.coerce.number().int().positive().max(50).default(12).optional(),
});
